import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Mic } from "lucide-react";
import TagVisual from "./TagVisual";

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-36 md:pt-48 pb-28 md:pb-36 overflow-hidden"
    >
      {/* Aurora background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-16 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] glow-blue opacity-45 blur-[120px]" />
        <div className="absolute top-48 right-10 w-80 h-80 glow-silver blur-2xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.9),transparent_60%)]" />
        <div className="absolute inset-0 opacity-[0.03] [background-image:linear-gradient(to_right,#000_1px,transparent_1px),linear-gradient(to_bottom,#000_1px,transparent_1px)] [background-size:56px_56px]" />
      </div>

      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs tracking-[0.22em] uppercase text-zinc-700"
            data-testid="hero-badge"
          >
            <Mic className="w-3.5 h-3.5 text-blue-500" />
            Talk to your stuff
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-10 font-display text-[44px] leading-[1.02] sm:text-6xl lg:text-[88px] lg:leading-[0.98] font-semibold tracking-[-0.03em] max-w-5xl"
            data-testid="hero-headline"
          >
            Call it.
            <span className="italic font-light text-zinc-500"> Find it.</span>
            <br className="hidden sm:block" /> Done.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-7 max-w-xl text-lg md:text-xl text-zinc-600 leading-relaxed"
            data-testid="hero-subhead"
          >
            A responsive little object that listens for your voice — and answers back.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-10 flex flex-col sm:flex-row items-center gap-3"
          >
            <Button
              data-testid="hero-cta-waitlist"
              onClick={() => scrollTo("waitlist")}
              className="group rounded-full bg-black hover:bg-zinc-900 text-white px-8 text-[15px] font-medium cta-shine shadow-[0_12px_30px_-8px_rgba(0,0,0,0.35)] hover:-translate-y-[1px] transition"
              style={{ height: "52px" }}
            >
              Join the Waitlist
              <ArrowRight className="ml-1.5 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button
              data-testid="hero-cta-early"
              onClick={() => scrollTo("interaction")}
              variant="ghost"
              className="rounded-full h-[52px] px-7 text-zinc-700 hover:text-black hover:bg-transparent"
            >
              See it respond →
            </Button>
          </motion.div>

          <p className="mt-6 text-xs md:text-sm text-zinc-500 tracking-wide" data-testid="hero-trust">
            No app. No searching. Just response.
          </p>

          {/* Tag with dialogue hint */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.4, ease: "easeOut" }}
            className="relative mt-20 md:mt-24"
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="absolute -left-4 md:-left-20 top-10 glass rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm text-zinc-700 hidden sm:block"
              data-testid="hero-bubble-user"
            >
              <span className="text-[10px] tracking-[0.22em] uppercase text-zinc-400 block">You</span>
              "Hey, where are my keys?"
            </motion.div>

            <TagVisual size={440} listening />

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.8 }}
              className="absolute -right-4 md:-right-28 bottom-24 glass rounded-2xl rounded-br-sm px-4 py-2.5 text-sm text-zinc-900 hidden sm:block"
              data-testid="hero-bubble-tag"
            >
              <span className="text-[10px] tracking-[0.22em] uppercase text-blue-500 block">VoloTag</span>
              "Right by the sofa."
            </motion.div>

            <div className="mx-auto w-80 h-14 rounded-[50%] bg-gradient-to-b from-zinc-300/60 to-transparent blur-2xl -mt-6" />
          </motion.div>

          {/* Trust bar */}
          <div
            data-testid="trust-bar"
            className="mt-16 md:mt-20 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs tracking-[0.18em] uppercase text-zinc-500"
          >
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Early access launching soon</span>
            <span className="hidden md:inline-block w-px h-3 bg-zinc-300" />
            <span>Limited first batch</span>
            <span className="hidden md:inline-block w-px h-3 bg-zinc-300" />
            <span>Backed by Verified Digital Solutions</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
